import { Link } from "react-router-dom";
import { AlertTriangle, ArchiveX, FlaskConical, Tag } from "lucide-react";
import type { RecipeSummary } from "@/lib/types";
import { pluginLabel } from "@/lib/types";

export default function RecipeCard({ recipe }: { recipe: RecipeSummary }) {
  return (
    <article className="group relative rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 hover:border-brand-400 dark:hover:border-brand-500 hover:-translate-y-0.5 transition motion-reduce:transform-none motion-reduce:transition-none">
      <header className="flex items-start gap-2">
        <h3 className="text-base font-semibold leading-snug flex-1">
          <Link
            to={`/recipes/${recipe.id}`}
            className="text-slate-900 dark:text-slate-100 hover:text-brand-700 dark:hover:text-brand-300 after:absolute after:inset-0 after:content-['']"
          >
            {recipe.title}
          </Link>
        </h3>
        {recipe.status === "draft" ? (
          <span
            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200 font-medium"
            title="This recipe has not been run against a live Cowork tenant yet."
          >
            <FlaskConical size={10} aria-hidden /> draft
          </span>
        ) : null}
        {recipe.mutates_data ? (
          <span
            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200"
            title="This recipe creates or modifies Dynamics 365 data — test in a sandbox first."
          >
            <AlertTriangle size={10} aria-hidden /> mutates data
          </span>
        ) : null}
        {recipe.deprecated ? (
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-900 dark:bg-red-950/60 dark:text-red-200">
            <ArchiveX size={10} aria-hidden /> deprecated
          </span>
        ) : null}
      </header>
      <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 line-clamp-3">
        {recipe.summary}
      </p>
      <footer className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400">
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800">
          <Tag size={10} aria-hidden /> {pluginLabel(recipe.plugin)}
        </span>
        {recipe.process_tags.slice(0, 2).map((t) => (
          <span
            key={t}
            className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 truncate max-w-[12rem]"
            title={t}
          >
            {t.split("/").slice(-1)[0]}
          </span>
        ))}
        <span className="ml-auto whitespace-nowrap">v{recipe.version}</span>
      </footer>
    </article>
  );
}
